import { COMPANY_ACCOUNT_KINDS, COMPANY_ACCOUNT_KIND_LABELS, type CompanyAccountInput } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input, Select } from 'antd';
import { useEffect } from 'react';
import type { CompanyAccountView } from '../company-accounts.api';
import { MPESA_NUMBER_RULE, toCompanyAccountFormValues, toCompanyAccountInput, type CompanyAccountFormValues } from '../policies/company-account-form';

const KIND_OPTIONS = COMPANY_ACCOUNT_KINDS.map((kind) => ({ value: kind, label: COMPANY_ACCOUNT_KIND_LABELS[kind] }));
const required = (message: string) => ({ required: true, whitespace: true, message });

interface CompanyAccountDrawerProps {
  open: boolean;
  /** The unused account being edited; absent when adding. */
  editing?: CompanyAccountView;
  error: string | null;
  submitting: boolean;
  onSave: (input: CompanyAccountInput) => Promise<boolean>;
  onClose: () => void;
}

/** Kind first, since it decides which numbers the account has. A refusal from the server shows at the top and the values stay. */
export function CompanyAccountDrawer({ open, editing, error, submitting, onSave, onClose }: CompanyAccountDrawerProps) {
  const [form] = Form.useForm<CompanyAccountFormValues>();
  const kind = Form.useWatch('kind', form);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (editing) form.setFieldsValue(toCompanyAccountFormValues(editing));
  }, [open, editing, form]);

  async function submit(values: CompanyAccountFormValues) {
    if (await onSave(toCompanyAccountInput(values))) onClose();
  }

  return (
    <Drawer open={open} onClose={onClose} title={editing ? 'Edit account' : 'Add account'} placement="right" size={480} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<CompanyAccountFormValues> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="kind" label="Kind" rules={[{ required: true, message: 'Choose the kind of account' }]}>
          <Select options={KIND_OPTIONS} placeholder="Bank account, M-Pesa paybill or till" />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[required('Enter a name people will recognise')]}>
          <Input placeholder="e.g. Equity main collections" />
        </Form.Item>
        {kind === 'bank' && (
          <>
            <Form.Item name="bankName" label="Bank" rules={[required('Enter the bank')]}>
              <Input />
            </Form.Item>
            <Form.Item name="accountNumber" label="Account number" rules={[required('Enter the account number')]}>
              <Input />
            </Form.Item>
          </>
        )}
        {kind === 'mpesa_paybill' && (
          <Form.Item name="paybillNumber" label="Paybill number" rules={[required('Enter the paybill number'), MPESA_NUMBER_RULE]}>
            <Input inputMode="numeric" />
          </Form.Item>
        )}
        {kind === 'mpesa_till' && (
          <Form.Item name="tillNumber" label="Till number" rules={[required('Enter the till number'), MPESA_NUMBER_RULE]}>
            <Input inputMode="numeric" />
          </Form.Item>
        )}
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            {editing ? 'Save changes' : 'Add account'}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
